import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { timer } from 'rxjs';
import { catchError, map, switchMap, takeUntil } from 'rxjs/operators';
import { logout, openNotificationsDrawer, userAuthenticated } from '@capital-access/common/actions';
import { CommonLazyLoadingService } from '@capital-access/common/lazy-loading';
import {
  buildGroupedNotificationListRequest,
  NOTIFICATION_COUNT_REFRESH_INTERVAL_MIN,
  NOTIFICATION_TYPE_CONFIGS,
  NotificationsDrawerActions,
  NotificationsDrawerModule,
  NotificationsHubService
} from '@capital-access/notifications/drawer';
import {
  LOAD_NOTIFICATIONS_DRAWER_MODULE,
  NOTIFICATIONS_DRAWER_MODULE_ID
} from '../notifications-drawer-module-id.model';

@Injectable()
export class NotificationsDrawerLazyEffects {
  openNotificationsDrawer$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(openNotificationsDrawer),
        switchMap(() =>
          this.lazyLoadingService
            .loadModule(NOTIFICATIONS_DRAWER_MODULE_ID, LOAD_NOTIFICATIONS_DRAWER_MODULE)
            .pipe(switchMap(moduleRef => [(moduleRef.instance as NotificationsDrawerModule).openDrawer()]))
        )
      ),
    { dispatch: false }
  );

  onUserAuthenticated$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userAuthenticated),
      switchMap(() =>
        timer(0, NOTIFICATION_COUNT_REFRESH_INTERVAL_MIN * 60 * 1000).pipe(
          map(() => NotificationsDrawerActions.loadNotificationCount()),
          takeUntil(this.actions$.pipe(ofType(logout)))
        )
      )
    )
  );

  loadNotificationCount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotificationsDrawerActions.loadNotificationCount),
      switchMap(() => {
        const defaultLimits: Record<string, number> = {};
        for (const [code, config] of Object.entries(NOTIFICATION_TYPE_CONFIGS)) {
          defaultLimits[code] = config.initialLimit;
        }
        return this.notificationsService.getNotificationCounts(buildGroupedNotificationListRequest(defaultLimits)).pipe(
          map(response =>
            NotificationsDrawerActions.loadNotificationCountSuccess({ unreadCount: response.unreadCount })
          ),
          catchError(error => [
            NotificationsDrawerActions.loadNotificationCountFailure({
              error: error.message ?? 'Failed to load notification count'
            })
          ])
        );
      })
    )
  );

  constructor(
    private actions$: Actions,
    private lazyLoadingService: CommonLazyLoadingService,
    private notificationsService: NotificationsHubService
  ) {}
}
